import { useCallback, useMemo } from 'react';
import { ERROR_CODES, SCREENS } from '../../services/exports/Constants';
import { ErrorHandlerInterface } from '../../services/exports/Interfaces';
import useAppMethods from '../utility/useAppMethods';
import useRouter from '../navigation/useRouter';
import useMobileApp from '../mobile-app/useMobileApp';

/**
 * 412 error codes the backend raises when the CHOSEN ORDER METHOD is what is wrong — the slot is
 * gone, the channel is closed/paused, the address is out of zone, the quote expired. The customer
 * fixes every one of them in the order-method modal, so that is where they should land.
 *
 * Without a handler for the code, `BackendApiClient.errorHandler` opens the global InfoModal with
 * no `onConfirm`, and `InfoModal` then falls back to navigating HOME — dropping the customer out of
 * checkout with no hint of what to change.
 */
export const ORDER_METHOD_ERROR_CODES: string[] = [
  ERROR_CODES.ORDER_TIME_NOT_AVAILABLE,
  ERROR_CODES.ORDER_SLOT_IS_TAKEN,
  ERROR_CODES.ORDERS_PAUSED,
  ERROR_CODES.COMPANY_CLOSED,
  ERROR_CODES.PICKUP_CLOSED,
  ERROR_CODES.DELIVERY_CLOSED,
  ERROR_CODES.DELIVERY_UNAVAILABLE,
  ERROR_CODES.ADDRESS_OUTSIDE_DELIVERY_ZONE,
  ERROR_CODES.NO_DELIVERY_QUOTES_AVAILABLE,
  ERROR_CODES.ADDRESS_NOT_FOUND,
  ERROR_CODES.DELIVERY_QUOTE_ALREADY_USED,
  ERROR_CODES.ROOM_SERVICE_LOCATION_UNAVAILABLE,
  ERROR_CODES.ROOM_SERVICE_UNAVAILABLE,
];

const isOnCheckoutScreen = (): boolean => window.location.pathname.split('/').includes(SCREENS.CHECKOUT);

/**
 * Error handlers to pass to `OrderValidationManager.validate` / `createOrder` so an order-method
 * 412 keeps the customer in the checkout flow:
 *
 *  * already on /checkout  -> stay put, open the order-method modal
 *  * anywhere else (/payment) -> go back to /checkout, then open the order-method modal
 *
 * The handlers run when the customer confirms the InfoModal, so the backend's own explanation
 * ("We don't offer pickup at this time...") is still read first.
 *
 * Inside the native webview the map is EMPTY on purpose: the mobile module renders its own
 * OrderMethod modal from the caller's error branch, and `GlobalModalsStack` never mounts the web
 * modal there — a stale `openModal: true` would only block `useOrderMethodModalGate`'s reopen.
 * Returning no handlers also keeps `OrderFlowProvider.createOrder`'s defaults intact on mobile.
 */
export default function useOrderMethodErrorHandlers(): ErrorHandlerInterface {
  const { navigate } = useRouter();
  const { isMobileApp } = useMobileApp();
  const { toggleOrderMethodModal } = useAppMethods();

  const handle = useCallback((): void => {
    if (!isOnCheckoutScreen()) {
      navigate({
        web: {
          to: SCREENS.CHECKOUT,
        },
      });
    }

    toggleOrderMethodModal({ openModal: true });
  }, [navigate, toggleOrderMethodModal]);

  return useMemo(() => {
    if (isMobileApp) {
      return {};
    }

    return Object.fromEntries(ORDER_METHOD_ERROR_CODES.map((code) => [code, handle]));
  }, [isMobileApp, handle]);
}
